import { useCallback } from 'react'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useQueryClient } from 'react-query'
import { useUpdateBrandPlanSelectedPlatform } from './useUpdateBrandPlanSelectedPlatform'

interface UseSwitchPlatformOptions {
  oldIntegration?: ExternalIntegration
  tactic: 'seo' | 'email'
}

/**
 * Hook that provides a connect function to switch from one platform to another.
 * Passes the old integration ID to the API so it can handle deletion and other logic.
 */
export const useSwitchPlatform = ({ oldIntegration, tactic }: UseSwitchPlatformOptions) => {
  const { connect: originalConnect } = useConnectAccountPopup()
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const updateBrandPlanSelectedPlatform = useUpdateBrandPlanSelectedPlatform()
  const oldIntegrationId = oldIntegration?.id

  const connect = useCallback(
    async (popup: Window | undefined, options?: any) => {
      const switchFromIntegrationId = options?.switchFromIntegrationId ?? oldIntegrationId
      const wrappedOptions = {
        ...options,
        ...(switchFromIntegrationId && { switchFromIntegrationId }),
        onConnect: async (newIntegrations: ExternalIntegration[]) => {
          if (newIntegrations.length > 0) {
            await Promise.all(
              newIntegrations.map((integration) => updateBrandPlanSelectedPlatform(tactic, integration)),
            )
            queryClient.invalidateQueries(['externalIntegrations', workspaceId])
          }
          options?.onConnect?.(newIntegrations)
        },
      }

      return originalConnect(popup, wrappedOptions)
    },
    [originalConnect, oldIntegrationId, queryClient, workspaceId, updateBrandPlanSelectedPlatform, tactic],
  )

  return {
    connect,
  }
}
